/**
 * Settings → Tinker tab.
 *
 * T.0 surface for the Tinker (Thinking Machines Lab) LoRA fine-tuning
 * spike. Shows run history, the current default training settings
 * (read-only knobs surfaced for transparency), eval outputs from the
 * latest complete run, and a "How to run" recipe for the operator.
 *
 * SECURITY: The `TINKER_API_KEY` is server-side only — it lives in
 * Convex env + `.env.local` (gitignored). This tab NEVER reads the
 * key into the SPA bundle. All data shown here comes from the
 * `tinkerSpikeRuns` Convex table, populated by the Python spike's
 * `recordResult` mutation.
 *
 * Conventions (per memory):
 *   - Tailwind only (`feedback_use_tailwind`)
 *   - Black text on light bg (`feedback_no_light_grey_text`)
 *   - data-testid from `testIds.ts` (`feedback_data_testid`)
 *   - No write paths that could leak secrets
 */

import { useEffect, useState, type ReactElement } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';

// Defaults match `packages/repancy/spikes/tinker-poc/train_sft.py`
// AND `convex/tinkerTrainingSettings.ts` DEFAULTS.
const DEFAULTS = {
  baseModel: 'Qwen/Qwen3-8B',
  loraRank: 32,
  epochs: 3,
  learningRate: 2e-4,
  costCapUsd: 5.0,
  datasetScopeJson: '["firm-skills"]',
} as const;

const BASE_MODEL_OPTIONS = [
  'Qwen/Qwen3-8B',
  'Qwen/Qwen2.5-7B',
  'meta-llama/Llama-3.1-8B',
  'Qwen/Qwen3-VL',
];

const LORA_RANK_OPTIONS = [8, 16, 32, 64];

const DATASET_SCOPE_OPTIONS: ReadonlyArray<{
  key: string;
  label: string;
  enabled: boolean;
}> = [
  { key: 'firm-skills', label: 'Firm Skills (committed)', enabled: true },
  { key: 'vault-notes', label: 'Vault notes (T.2)', enabled: false },
  { key: 'audit-log', label: 'NIPE audit log (RL — T.2)', enabled: false },
];

interface TinkerRunRow {
  _id: string;
  runId: string;
  baseModel: string;
  loraRank: number;
  epochs: number;
  learningRate: number;
  datasetRowCount: number;
  status: 'running' | 'complete' | 'failed' | 'billing-blocked';
  finalAvgLoss?: number;
  costUsd?: number;
  errorMessage?: string;
  lossCurveJson?: string;
  evalOutputsJson?: string;
  /** T.1 — JSON-stringified array of skill names used in this run. */
  skillNamesUsedJson?: string;
  startedAt: number;
  completedAt?: number;
}

interface TinkerSettingsRow {
  _id: string;
  baseModel: string;
  loraRank: number;
  epochs: number;
  learningRate: number;
  costCapUsd: number;
  datasetScopeJson: string;
  updatedAt: number;
}

// T.2.a.3 — orchestrated retrain job row (subset of the Convex shape
// the SPA actually reads).
type TinkerJobStatus =
  | 'queued'
  | 'claimed'
  | 'running'
  | 'complete'
  | 'failed';

interface TinkerJobRow {
  _id: string;
  status: TinkerJobStatus;
  workerId?: string;
  errorMessage?: string;
  spikeRunRowId?: string;
  createdAt: number;
  claimedAt?: number;
  completedAt?: number;
}

interface EvalRow {
  prompt: string;
  baseline: string;
  lora: string;
}

function statusBadgeClass(status: TinkerRunRow['status']): string {
  switch (status) {
    case 'complete':
      return 'bg-green-100 text-green-900 border border-green-700';
    case 'running':
      return 'bg-yellow-100 text-yellow-900 border border-yellow-700';
    case 'failed':
      return 'bg-red-100 text-red-900 border border-red-700';
    case 'billing-blocked':
      return 'bg-orange-100 text-orange-900 border border-orange-700';
  }
}

function formatTimestamp(ms: number): string {
  return new Date(ms).toLocaleString();
}

export function TinkerTab(): ReactElement {
  const [showEval, setShowEval] = useState(false);

  const runs = useQuery(
    api.tinkerSpikeRuns.listRecent,
    { limit: 10 },
  ) as TinkerRunRow[] | undefined;

  const persistedSettings = useQuery(
    api.tinkerTrainingSettings.current,
    {},
  ) as TinkerSettingsRow | null | undefined;

  const upsertSettings = useMutation(api.tinkerTrainingSettings.upsert);

  // T.2.a.3 — orchestrated retrains
  const requestJob = useMutation(api.tinkerTrainingJobs.request);
  const kickWorker = useAction(api.tinkerTrainingJobs.kickWorker);
  const currentJob = useQuery(
    api.tinkerTrainingJobs.currentForUser,
    {},
  ) as TinkerJobRow | null | undefined;
  const [retrainStatus, setRetrainStatus] = useState<
    'idle' | 'requesting' | 'kicking' | 'queued' | 'error'
  >('idle');
  const [retrainError, setRetrainError] = useState<string | null>(null);

  // Form state — initialized from persisted settings (or defaults).
  const [baseModel, setBaseModel] = useState<string>(DEFAULTS.baseModel);
  const [loraRank, setLoraRank] = useState<number>(DEFAULTS.loraRank);
  const [epochs, setEpochs] = useState<number>(DEFAULTS.epochs);
  const [learningRate, setLearningRate] = useState<number>(DEFAULTS.learningRate);
  const [costCapUsd, setCostCapUsd] = useState<number>(DEFAULTS.costCapUsd);
  const [datasetScope, setDatasetScope] = useState<string[]>(['firm-skills']);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Sync form state from Convex when the persisted row first loads.
  useEffect(() => {
    if (!persistedSettings) return;
    setBaseModel(persistedSettings.baseModel);
    setLoraRank(persistedSettings.loraRank);
    setEpochs(persistedSettings.epochs);
    setLearningRate(persistedSettings.learningRate);
    setCostCapUsd(persistedSettings.costCapUsd);
    try {
      const parsed = JSON.parse(persistedSettings.datasetScopeJson);
      if (Array.isArray(parsed)) setDatasetScope(parsed);
    } catch {
      // ignore malformed; keep defaults
    }
  }, [persistedSettings]);

  const latestRun = runs?.[0];
  const evalRows: EvalRow[] = (() => {
    if (!latestRun?.evalOutputsJson) return [];
    try {
      return JSON.parse(latestRun.evalOutputsJson) as EvalRow[];
    } catch {
      return [];
    }
  })();
  const latestSkillsUsed: string[] = (() => {
    if (!latestRun?.skillNamesUsedJson) return [];
    try {
      const parsed = JSON.parse(latestRun.skillNamesUsedJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  async function handleSave() {
    setSaveStatus('saving');
    try {
      await upsertSettings({
        baseModel,
        loraRank,
        epochs,
        learningRate,
        costCapUsd,
        datasetScopeJson: JSON.stringify(datasetScope),
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch {
      setSaveStatus('error');
    }
  }

  function handleReset() {
    setBaseModel(DEFAULTS.baseModel);
    setLoraRank(DEFAULTS.loraRank);
    setEpochs(DEFAULTS.epochs);
    setLearningRate(DEFAULTS.learningRate);
    setCostCapUsd(DEFAULTS.costCapUsd);
    setDatasetScope(['firm-skills']);
  }

  function toggleDatasetScope(key: string, enabled: boolean) {
    if (!enabled) return;
    setDatasetScope((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }

  /**
   * T.2.a.3 — orchestrated retrain.
   *
   * Snapshots current form values into a tinkerTrainingJobs row, then
   * invokes the kickWorker action which POSTs to the
   * repancy-tinker-trainer Function App. The status banner below
   * subscribes to currentForUser and renders the live lifecycle.
   */
  async function handleRetrainNow(): Promise<void> {
    setRetrainError(null);
    setRetrainStatus('requesting');
    try {
      const jobId = await requestJob({
        baseModel,
        loraRank,
        epochs,
        learningRate,
        costCapUsd,
        datasetScopeJson: JSON.stringify(datasetScope),
      });
      setRetrainStatus('kicking');
      const result = await kickWorker({
        jobId,
      });
      if (!result.ok) {
        setRetrainStatus('error');
        setRetrainError(
          result.reason ?? 'Worker unreachable. See logs for details.',
        );
        return;
      }
      setRetrainStatus('queued');
      // Live job state takes over from here via the currentJob subscription.
    } catch (err) {
      setRetrainStatus('error');
      setRetrainError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div
      data-testid={testIds.settings.tinkerPanel}
      className="text-black text-xs leading-relaxed"
    >
      <div className="mb-3 p-2 bg-[#fffbcc] border border-[#b08800]">
        <strong>Operator-editable specialization settings.</strong> Tinker LoRA
        fine-tuning for firm Skills. The API key is server-side only; this UI
        edits a Convex row that the Python spike reads on the next run. See{' '}
        <a
          href="https://tinker-docs.thinkingmachines.ai"
          target="_blank"
          rel="noreferrer"
          className="underline text-blue-800"
        >
          Tinker docs
        </a>
        {' · '}
        <a
          href="https://tinker-console.thinkingmachines.ai/billing/balance"
          target="_blank"
          rel="noreferrer"
          className="underline text-blue-800"
        >
          Billing
        </a>
        .
      </div>

      {/* Editable training settings form */}
      <section
        data-testid={testIds.settings.tinkerSettingsForm}
        className="mb-3 p-2 bg-white border border-gray-700"
      >
        <h3 className="font-bold text-xs mb-2">Specialization settings</h3>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 items-center">
          <label className="text-xs" htmlFor="tinker-base-model">Base model</label>
          <select
            id="tinker-base-model"
            data-testid={testIds.settings.tinkerBaseModelInput}
            value={baseModel}
            onChange={(e) => setBaseModel(e.target.value)}
            className="text-xs px-1 py-0.5 border border-gray-700 bg-white text-black"
          >
            {BASE_MODEL_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>

          <label className="text-xs" htmlFor="tinker-lora-rank">LoRA rank</label>
          <select
            id="tinker-lora-rank"
            data-testid={testIds.settings.tinkerLoraRankInput}
            value={loraRank}
            onChange={(e) => setLoraRank(Number(e.target.value))}
            className="text-xs px-1 py-0.5 border border-gray-700 bg-white text-black w-24"
          >
            {LORA_RANK_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>

          <label className="text-xs" htmlFor="tinker-epochs">Epochs</label>
          <input
            id="tinker-epochs"
            data-testid={testIds.settings.tinkerEpochsInput}
            type="number"
            min={1}
            max={10}
            value={epochs}
            onChange={(e) => setEpochs(Math.max(1, Math.min(10, Number(e.target.value))))}
            className="text-xs px-1 py-0.5 border border-gray-700 bg-white text-black w-20"
          />

          <label className="text-xs" htmlFor="tinker-learning-rate">Learning rate</label>
          <input
            id="tinker-learning-rate"
            data-testid={testIds.settings.tinkerLearningRateInput}
            type="number"
            step="0.00001"
            min={1e-5}
            max={1e-3}
            value={learningRate}
            onChange={(e) => setLearningRate(Number(e.target.value))}
            className="text-xs px-1 py-0.5 border border-gray-700 bg-white text-black w-28"
          />

          <label className="text-xs" htmlFor="tinker-cost-cap">Cost cap (USD)</label>
          <input
            id="tinker-cost-cap"
            data-testid={testIds.settings.tinkerCostCapInput}
            type="number"
            step="0.10"
            min={0.10}
            max={50}
            value={costCapUsd}
            onChange={(e) => setCostCapUsd(Number(e.target.value))}
            className="text-xs px-1 py-0.5 border border-gray-700 bg-white text-black w-20"
          />
        </div>

        <div className="mt-2">
          <div className="text-xs font-bold mb-1">Dataset scope</div>
          <div className="space-y-0.5">
            {DATASET_SCOPE_OPTIONS.map((opt) => (
              <label
                key={opt.key}
                data-testid={testIds.settings.tinkerDatasetScopeOption(opt.key)}
                className={`flex items-center gap-1 text-xs ${
                  opt.enabled ? '' : 'text-gray-700'
                }`}
              >
                <input
                  type="checkbox"
                  disabled={!opt.enabled}
                  checked={datasetScope.includes(opt.key)}
                  onChange={() => toggleDatasetScope(opt.key, opt.enabled)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-2 mt-3 items-center">
          <button
            type="button"
            data-testid={testIds.settings.tinkerSaveButton}
            onClick={() => void handleSave()}
            disabled={saveStatus === 'saving'}
            className="px-2 py-0.5 text-xs bg-blue-700 text-white border border-black hover:bg-blue-800 disabled:bg-gray-400 disabled:text-gray-700"
          >
            {saveStatus === 'saving' ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            data-testid={testIds.settings.tinkerResetButton}
            onClick={handleReset}
            className="px-2 py-0.5 text-xs bg-gray-200 hover:bg-gray-300 border border-black text-black"
          >
            Reset to defaults
          </button>
          <button
            type="button"
            data-testid={testIds.settings.tinkerRetrainButton}
            disabled={
              retrainStatus === 'requesting' || retrainStatus === 'kicking'
            }
            onClick={() => void handleRetrainNow()}
            className="px-2 py-0.5 text-xs bg-green-700 text-white border border-black hover:bg-green-800 disabled:bg-gray-400 disabled:text-gray-700"
          >
            {retrainStatus === 'requesting' || retrainStatus === 'kicking'
              ? 'Starting…'
              : 'Re-specialize now'}
          </button>
          {saveStatus === 'saved' && (
            <span
              data-testid={testIds.settings.tinkerSaveStatus}
              className="text-xs text-green-800 font-bold"
            >
              ✓ saved — re-run the spike to use these settings
            </span>
          )}
          {saveStatus === 'error' && (
            <span
              data-testid={testIds.settings.tinkerSaveStatus}
              className="text-xs text-red-800 font-bold"
            >
              ✗ save failed
            </span>
          )}
        </div>
        {/* T.2.a.3 — orchestrated retrain status banner. */}
        {(retrainStatus !== 'idle' || currentJob) && (
          <div
            data-testid={testIds.settings.tinkerJobStatusBanner}
            className={`mt-2 p-2 border text-xs ${
              currentJob?.status === 'complete'
                ? 'bg-green-100 border-green-700 text-green-900'
                : currentJob?.status === 'failed' || retrainStatus === 'error'
                  ? 'bg-red-100 border-red-700 text-red-900'
                  : 'bg-yellow-100 border-yellow-700 text-yellow-900'
            }`}
          >
            <strong>Latest job:</strong>{' '}
            <span data-testid={testIds.settings.tinkerRetrainStatus}>
              {retrainStatus === 'requesting' && 'Enqueueing…'}
              {retrainStatus === 'kicking' && 'Kicking worker…'}
              {retrainStatus !== 'requesting' &&
                retrainStatus !== 'kicking' &&
                currentJob &&
                currentJob.status}
              {retrainStatus === 'queued' && !currentJob && 'queued'}
              {retrainStatus === 'error' && 'error'}
              {retrainStatus === 'idle' && !currentJob && '—'}
            </span>
            {currentJob?.workerId && (
              <span className="ml-2 text-gray-800">
                worker: <code>{currentJob.workerId}</code>
              </span>
            )}
            {(retrainError || currentJob?.errorMessage) && (
              <div
                data-testid={testIds.settings.tinkerJobErrorBanner}
                className="mt-1"
              >
                {retrainError ?? currentJob?.errorMessage}
              </div>
            )}
          </div>
        )}
        <div className="text-xs mt-2 text-gray-800">
          Re-specialize now triggers the orchestrated Function App worker
          (T.2.a). Settings above are snapshot into the job row at
          request time; concurrent edits don't poison the run.
        </div>
      </section>

      {/* Skills used in the latest run */}
      {latestSkillsUsed.length > 0 && (
        <section
          data-testid={testIds.settings.tinkerSkillsUsed}
          className="mb-3 p-2 bg-white border border-gray-700"
        >
          <h3 className="font-bold text-xs mb-1">Skills used in latest run</h3>
          <div className="flex flex-wrap gap-1">
            {latestSkillsUsed.map((name) => (
              <span
                key={name}
                className="inline-block px-1.5 py-0.5 text-xs bg-green-100 border border-green-700 text-green-900 rounded-sm"
              >
                ✨ {name}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Run history */}
      <section className="mb-3 p-2 bg-white border border-gray-700">
        <h3 className="font-bold text-xs mb-2">Recent runs</h3>
        {runs === undefined && <div>Loading…</div>}
        {runs !== undefined && runs.length === 0 && (
          <div className="text-xs">
            No Tinker runs recorded yet. Run{' '}
            <code>python packages/repancy/spikes/tinker-poc/train_sft.py</code> to
            create the first record.
          </div>
        )}
        {runs !== undefined && runs.length > 0 && (
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-100 text-black border-b border-gray-700">
                <th className="text-left px-1 py-0.5">Started</th>
                <th className="text-left px-1 py-0.5">Status</th>
                <th className="text-right px-1 py-0.5">Avg loss</th>
                <th className="text-right px-1 py-0.5">Cost (USD)</th>
                <th className="text-right px-1 py-0.5">Rows</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr
                  key={r._id}
                  data-testid={testIds.settings.tinkerRunHistoryRow(r._id)}
                  className="border-b border-gray-300"
                >
                  <td className="px-1 py-0.5 font-mono">
                    {formatTimestamp(r.startedAt)}
                  </td>
                  <td className="px-1 py-0.5">
                    <span
                      data-testid={testIds.settings.tinkerStatusBadge}
                      className={`inline-block px-1 ${statusBadgeClass(r.status)}`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td
                    data-testid={testIds.settings.tinkerLastLossDisplay}
                    className="text-right px-1 py-0.5 font-mono"
                  >
                    {r.finalAvgLoss?.toFixed(4) ?? '—'}
                  </td>
                  <td
                    data-testid={testIds.settings.tinkerLastCostDisplay}
                    className="text-right px-1 py-0.5 font-mono"
                  >
                    {r.costUsd !== undefined ? `$${r.costUsd.toFixed(3)}` : '—'}
                  </td>
                  <td className="text-right px-1 py-0.5 font-mono">
                    {r.datasetRowCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {latestRun?.status === 'billing-blocked' && (
          <div className="mt-2 p-2 bg-orange-100 border border-orange-700 text-xs">
            <strong>Billing setup required.</strong>{' '}
            <a
              data-testid={testIds.settings.tinkerBillingHelpLink}
              href="https://tinker-console.thinkingmachines.ai/billing/balance"
              target="_blank"
              rel="noreferrer"
              className="underline text-blue-800"
            >
              Add payment method
            </a>{' '}
            to unblock training.
          </div>
        )}
        {latestRun?.errorMessage && latestRun.status === 'failed' && (
          <div className="mt-2 p-2 bg-red-100 border border-red-700 text-xs">
            <strong>Last error:</strong> {latestRun.errorMessage}
          </div>
        )}
      </section>

      {/* Eval outputs */}
      {latestRun && evalRows.length > 0 && (
        <section
          data-testid={testIds.settings.tinkerEvalOutputs}
          className="mb-3 p-2 bg-white border border-gray-700"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-xs">
              Eval outputs (latest run)
            </h3>
            <button
              type="button"
              onClick={() => setShowEval((v) => !v)}
              className="text-xs px-2 py-0.5 border border-black bg-gray-200 hover:bg-gray-300 text-black"
            >
              {showEval ? 'Hide' : 'Show'}
            </button>
          </div>
          {showEval && (
            <div className="max-h-96 overflow-y-auto space-y-2">
              {evalRows.map((row, i) => (
                <div key={i} className="border border-gray-300 p-1">
                  <div className="font-bold text-xs mb-1">Prompt {i + 1}</div>
                  <pre className="text-xs whitespace-pre-wrap bg-gray-50 p-1 border border-gray-300 mb-1">
                    {row.prompt}
                  </pre>
                  <div className="text-xs font-bold mt-1">Baseline (Qwen3-8B):</div>
                  <pre className="text-xs whitespace-pre-wrap bg-blue-50 p-1 border border-blue-300 mb-1">
                    {row.baseline}
                  </pre>
                  <div className="text-xs font-bold mt-1">LoRA (trained):</div>
                  <pre className="text-xs whitespace-pre-wrap bg-green-50 p-1 border border-green-300">
                    {row.lora}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* How-to-run recipe */}
      <section className="mb-3 p-2 bg-white border border-gray-700">
        <h3 className="font-bold text-xs mb-2">How to run a spike training pass</h3>
        <pre className="text-xs whitespace-pre-wrap bg-gray-50 p-1 border border-gray-300">{`cd packages/repancy/spikes/tinker-poc
node extract_skills_dataset.ts > dataset.jsonl
source .venv/bin/activate || (uv venv && source .venv/bin/activate && uv pip install tinker python-dotenv)
python train_sft.py
python evaluate.py`}</pre>
        <div className="text-xs mt-2 text-gray-800">
          The Python scripts read <code>TINKER_API_KEY</code> from{' '}
          <code>packages/repancy/.env.local</code> (gitignored). Total cost cap
          for this spike is <strong>$5</strong> per <code>~/.claude/plans/</code>{' '}
          decision.
        </div>
      </section>
    </div>
  );
}
